import { AI_SERVICES_ORIGIN } from "../config/apiUrls";
import { readFavouriteTransparencyFlag } from "./caricatureHobbyService";
import { fetchImage } from "./imageReadService";
import {
  findPreferredProductColor,
  getProductColorLabel,
  getProductDisplayName,
  sortProductColorsLightFirst,
  type Product,
} from "./productService";

const MERCH_GENERATE_PATH = "/creative-system/merch/generate";
const MERCH_GENERATE_CACHE_PREFIX = "merchGenerateCache:v2:";
/** Creative-system product ids that should be generated with API `pad=true`. */
const STICKER_SHEET_PRODUCT_KEY = "sticker-sheet";
const SINGLE_STICKER_PRODUCT_KEY = "sticker";

/** Single sticker, N-stickers packs, and sticker sheet use padded generate. */
function isPaddedStickerProductKey(productKey: string): boolean {
  if (!productKey) return false;
  if (productKey === STICKER_SHEET_PRODUCT_KEY) return true;
  if (productKey === SINGLE_STICKER_PRODUCT_KEY) return true;
  return /^stickers-\d+$/.test(productKey);
}

export type MerchGenerateColors = Record<string, string[]>;

export type MerchGenerateItem = {
  id?: string;
  color?: string | null;
  color_label?: string | null;
  image_url?: string;
  /** N-sticker packs: cutout/single art URL for orders (not the pack collage mockup). */
  image_url_single_sticker?: string;
  label?: string;
  variant_id?: string;
  [key: string]: unknown;
};

export type MerchGenerateResponse = {
  success: boolean;
  message?: string;
  error?: string;
  items?: MerchGenerateItem[];
  merch_count?: number;
  image_url?: string;
};

/** In-memory cache for the current kiosk tab (faster than sessionStorage). */
const merchGenerateMemoryCache = new Map<string, MerchGenerateResponse>();

export type MerchGeneratePayload = {
  products: string[];
  colors: MerchGenerateColors;
};

function slugifyToken(value: string): string {
  return value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

/**
 * Map catalog merchandise to creative-system merch product ids
 * (e.g. "T Shirt" → "tshirt", "Poster" → "canvas").
 */
export function resolveMerchGenerateProductKey(product: Product): string | null {
  const name = getProductDisplayName(product.name).toLowerCase();
  const sku = String(product.sku_code || "")
    .trim()
    .toLowerCase();
  const haystack = `${name} ${sku}`.trim();
  if (!haystack) return null;

  if (haystack.includes("sticker sheet") || sku === "sts") return "sticker-sheet";
  // "4 Stickers" → stickers-4, "6 Stickers" → stickers-6, etc.
  const stickersCountMatch = name.match(/\b(\d+)\s*stickers?\b/);
  if (stickersCountMatch) {
    return `stickers-${stickersCountMatch[1]}`;
  }
  if (name.includes("stickers")) {
    const countFromName = name.match(/(\d+)/);
    if (countFromName) return `stickers-${countFromName[1]}`;
  }
  if (haystack.includes("sticker") || sku === "st") return "sticker";

  if (haystack.includes("hoodie") || sku === "hd" || sku.startsWith("hd")) {
    return "hoodie";
  }
  if (
    haystack.includes("t shirt") ||
    haystack.includes("t-shirt") ||
    haystack.includes("tshirt") ||
    sku === "ts" ||
    sku.startsWith("ts")
  ) {
    return "tshirt";
  }
  if (haystack.includes("tote") || sku === "tb" || sku.startsWith("tb")) {
    return "tote";
  }
  if (/\bcap\b/.test(haystack) || sku === "cp" || sku.startsWith("cp")) {
    return "cap";
  }
  if (
    haystack.includes("photo paper") ||
    haystack.includes("photo print") ||
    haystack.includes("photopaper") ||
    sku === "ppp" ||
    sku === "pp" ||
    sku.startsWith("pp")
  ) {
    return "photo-print";
  }
  if (
    haystack.includes("poster") ||
    haystack.includes("canvas") ||
    sku === "ps" ||
    sku === "ca" ||
    sku.startsWith("ca")
  ) {
    return "canvas";
  }

  const slug = slugifyToken(name);
  return slug || null;
}

/** Map catalog color labels to values expected by /creative-system/merch/generate. */
function resolveMerchGenerateColorValue(colorLabel: string): string {
  const label = String(colorLabel || "").trim().toLowerCase();
  if (label === "ash") return "#b2b2b2";
  return label;
}

export function collectProductColorNames(product: Product): string[] {
  const colors = Array.isArray(product.color) ? product.color : [];
  const seen = new Set<string>();
  const out: string[] = [];

  colors.forEach((color) => {
    const label = getProductColorLabel(color).toLowerCase();
    if (!label) return;
    const value = resolveMerchGenerateColorValue(label);
    if (!value || seen.has(value)) return;
    seen.add(value);
    out.push(value);
  });

  return out;
}

/** Build products[] + colors{} from the merchandise catalog response. */
export function buildMerchGeneratePayload(products: Product[]): MerchGeneratePayload {
  const productKeys: string[] = [];
  const colors: MerchGenerateColors = {};
  const seen = new Set<string>();

  products.forEach((product) => {
    const key = resolveMerchGenerateProductKey(product);
    if (!key || seen.has(key)) return;
    seen.add(key);
    productKeys.push(key);

    const colorNames = collectProductColorNames(product);
    if (colorNames.length > 0) {
      colors[key] = colorNames;
    }
  });

  return { products: productKeys, colors };
}

async function artworkToFile(artwork: string | Blob | File): Promise<File> {
  if (artwork instanceof File) return artwork;
  if (artwork instanceof Blob) {
    return new File([artwork], "artwork.png", {
      type: artwork.type || "image/png",
    });
  }

  const response = await fetchImage(artwork);
  const blob = await response.blob();
  const type = blob.type || "image/png";
  const ext = type.includes("jpeg") || type.includes("jpg") ? "jpg" : "png";
  return new File([blob], `artwork.${ext}`, { type });
}

function artworkCacheToken(artwork: string | Blob | File): string {
  if (typeof artwork === "string") {
    const trimmed = artwork.trim();
    // Keep data URLs short but stable enough for cache keys.
    if (trimmed.startsWith("data:")) {
      return `data:${trimmed.length}:${trimmed.slice(0, 96)}:${trimmed.slice(-48)}`;
    }
    return trimmed;
  }
  if (artwork instanceof File) {
    return `file:${artwork.name}:${artwork.size}:${artwork.lastModified}`;
  }
  return `blob:${artwork.size}:${artwork.type}`;
}

export function buildMerchGenerateCacheKey(
  artwork: string | Blob | File,
  products: Product[],
  station = "merch",
  isTransparent = false,
  alreadyTransparent = true
): string {
  const { products: productKeys, colors } = buildMerchGeneratePayload(products);
  const hasStickerPad = productKeys.some(isPaddedStickerProductKey);
  return [
    artworkCacheToken(artwork),
    station,
    isTransparent ? "transparent" : "opaque",
    alreadyTransparent ? "already-transparent" : "not-already-transparent",
    hasStickerPad ? "sticker-pad" : "no-sticker-pad",
    productKeys.join(","),
    JSON.stringify(colors),
  ].join("::");
}

function readMerchGenerateCache(cacheKey: string): MerchGenerateResponse | null {
  const fromMemory = merchGenerateMemoryCache.get(cacheKey);
  if (fromMemory?.success && fromMemory.items?.length) return fromMemory;

  try {
    const raw = sessionStorage.getItem(MERCH_GENERATE_CACHE_PREFIX + cacheKey);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as MerchGenerateResponse;
    if (!parsed?.success || !Array.isArray(parsed.items) || parsed.items.length === 0) {
      return null;
    }
    merchGenerateMemoryCache.set(cacheKey, parsed);
    return parsed;
  } catch {
    return null;
  }
}

/** Sync cache lookup — used to restore merch grid immediately on back navigation. */
export function getCachedMerchGenerateResponse(
  artwork: string | Blob | File,
  products: Product[],
  station = "merch",
  isTransparent = false,
  alreadyTransparent = true
): MerchGenerateResponse | null {
  return readMerchGenerateCache(
    buildMerchGenerateCacheKey(
      artwork,
      products,
      station,
      isTransparent,
      alreadyTransparent
    )
  );
}

const PRINT_FRAME_MERCH_PAGE_CACHE_KEY = "printFrameMerchPageCache:v1";

export type PrintFrameMerchPageCache = {
  artKey: string;
  catalog: Product[];
  merchandise: Product[];
  packs: unknown[];
};

export function readPrintFrameMerchPageCache(
  artKey: string
): PrintFrameMerchPageCache | null {
  const key = artKey.trim();
  if (!key) return null;
  try {
    const raw = sessionStorage.getItem(PRINT_FRAME_MERCH_PAGE_CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as PrintFrameMerchPageCache;
    if (
      parsed?.artKey !== key ||
      !Array.isArray(parsed.merchandise) ||
      parsed.merchandise.length === 0
    ) {
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

export function writePrintFrameMerchPageCache(
  cache: PrintFrameMerchPageCache
): void {
  if (!cache.artKey.trim() || !cache.merchandise?.length) return;
  try {
    sessionStorage.setItem(PRINT_FRAME_MERCH_PAGE_CACHE_KEY, JSON.stringify(cache));
  } catch {
    /* ignore quota */
  }
}

export function clearPrintFrameMerchPageCache(): void {
  try {
    sessionStorage.removeItem(PRINT_FRAME_MERCH_PAGE_CACHE_KEY);
  } catch {
    /* ignore */
  }
}

function writeMerchGenerateCache(
  cacheKey: string,
  response: MerchGenerateResponse
): void {
  if (!response.success || !response.items?.length) return;
  merchGenerateMemoryCache.set(cacheKey, response);
  try {
    sessionStorage.setItem(
      MERCH_GENERATE_CACHE_PREFIX + cacheKey,
      JSON.stringify({
        success: true,
        message: response.message,
        items: response.items,
        merch_count: response.merch_count,
        image_url: response.image_url,
      })
    );
  } catch {
    // Quota / private mode — memory cache still works for this session.
  }
}

/** Warm the browser image cache so mockups paint quickly on the grid. */
export function preloadMerchGenerateImages(
  items: MerchGenerateItem[] | null | undefined
): void {
  if (!Array.isArray(items)) return;
  const seen = new Set<string>();
  items.forEach((item) => {
    const url = typeof item?.image_url === "string" ? item.image_url.trim() : "";
    if (!url || seen.has(url)) return;
    seen.add(url);
    const img = new Image();
    img.decoding = "async";
    img.src = url;
  });
}

export function clearMerchGenerateCache(): void {
  merchGenerateMemoryCache.clear();
  clearPrintFrameMerchPageCache();
  try {
    const keysToRemove: string[] = [];
    for (let i = 0; i < sessionStorage.length; i += 1) {
      const key = sessionStorage.key(i);
      if (key?.startsWith(MERCH_GENERATE_CACHE_PREFIX)) keysToRemove.push(key);
    }
    keysToRemove.forEach((key) => sessionStorage.removeItem(key));
  } catch {
    /* ignore */
  }
}

/**
 * Merge generate API `items` into catalog products so list/modals show mockup images.
 * Sets `merchGeneratedMockup` so UI can skip a second artwork overlay.
 */
export function applyMerchGenerateItemsToProducts(
  products: Product[],
  items: MerchGenerateItem[] | null | undefined
): Product[] {
  if (!Array.isArray(products) || products.length === 0) return products;
  if (!Array.isArray(items) || items.length === 0) return products;

  const byKey = new Map<string, MerchGenerateItem[]>();
  items.forEach((item) => {
    const key = String(item?.id || "")
      .trim()
      .toLowerCase();
    const imageUrl = typeof item?.image_url === "string" ? item.image_url.trim() : "";
    if (!key || !imageUrl) return;
    const list = byKey.get(key) || [];
    list.push(item);
    byKey.set(key, list);
  });

  return products.map((product) => {
    const key = resolveMerchGenerateProductKey(product);
    if (!key) return product;
    const matched = byKey.get(key);
    if (!matched?.length) return product;

    const defaultImage =
      matched.find((item) => !item.color)?.image_url?.trim() ||
      matched[0]?.image_url?.trim() ||
      "";

    const nextColors = Array.isArray(product.color)
      ? product.color.map((color) => {
          if (color == null) return color;
          const label = getProductColorLabel(color).toLowerCase();
          if (!label) return color;

          const colorMatch = matched.find((item) => {
            const itemColor = String(item.color || item.color_label || "")
              .trim()
              .toLowerCase();
            return itemColor === label;
          });
          const imageUrl = colorMatch?.image_url?.trim();
          if (!imageUrl) return color;

          if (typeof color === "string") {
            return {
              name: color,
              productImageUrl: imageUrl,
              merchGeneratedMockup: true,
            };
          }
          return {
            ...color,
            productImageUrl: imageUrl,
            product_image_url: imageUrl,
            merchGeneratedMockup: true,
          };
        })
      : product.color;

    const preferredColor = findPreferredProductColor(nextColors, product);
    const preferredColorImage =
      preferredColor && typeof preferredColor === "object"
        ? String(
            (preferredColor as { productImageUrl?: string }).productImageUrl ||
              ""
          ).trim()
        : "";

    const lightFirstImages = Array.isArray(nextColors)
      ? sortProductColorsLightFirst(nextColors)
          .map((color) =>
            color && typeof color === "object"
              ? String(
                  (color as { productImageUrl?: string }).productImageUrl || ""
                ).trim()
              : ""
          )
          .filter(Boolean)
      : [];

    // Prefer light color mockup (tote → beige, tee/cap → white, hoodie → ash).
    const displayImage =
      preferredColorImage || lightFirstImages[0] || defaultImage;
    if (!displayImage) return product;

    // N-sticker packs expose a dedicated single-sticker URL for order lines.
    const singleStickerUrl =
      matched
        .map((item) =>
          typeof item?.image_url_single_sticker === "string"
            ? item.image_url_single_sticker.trim()
            : ""
        )
        .find(Boolean) || "";

    return {
      ...product,
      color: nextColors,
      picture: displayImage,
      imgURL: displayImage,
      image_url: displayImage,
      ...(singleStickerUrl
        ? { image_url_single_sticker: singleStickerUrl }
        : {}),
      merchGeneratedMockup: true,
    };
  });
}

/** In-flight generate requests — StrictMode / remounts share one network call. */
const merchGenerateInFlight = new Map<string, Promise<MerchGenerateResponse>>();

async function requestMerchGenerate(options: {
  base: string;
  artworkFile: File;
  station: string;
  productKeys: string[];
  colors: MerchGenerateColors;
  isTransparent: boolean;
  /** Defaults true; pet caricature TEMP test uses false. */
  alreadyTransparent?: boolean;
  /** When sticker products are included, API pads artwork for those ids. */
  pad?: boolean;
}): Promise<MerchGenerateResponse> {
  const alreadyTransparent = options.alreadyTransparent !== false;
  const form = new FormData();
  form.append("artwork", options.artworkFile);
  form.append("station", options.station);
  form.append("products", JSON.stringify(options.productKeys));
  form.append("colors", JSON.stringify(options.colors));
  form.append("is_logo", "true");
  form.append("already_transparent", alreadyTransparent ? "true" : "false");
  if (options.isTransparent) {
    form.append("is_transparent", "true");
    form.append("isTransparent", "true");
  }
  // Single request: pad when any sticker / stickers-N / sticker-sheet is present.
  if (options.pad) {
    form.append("pad", "true");
  }

  const response = await fetch(`${options.base}${MERCH_GENERATE_PATH}`, {
    method: "POST",
    body: form,
  });

  let data: MerchGenerateResponse | null = null;
  try {
    data = (await response.json()) as MerchGenerateResponse;
  } catch {
    data = null;
  }

  if (!response.ok) {
    return {
      success: false,
      error:
        data?.error ||
        data?.message ||
        `Merch generate failed (${response.status})`,
      items: data?.items,
    };
  }

  return {
    success: Boolean(data?.success ?? true),
    message: data?.message,
    error: data?.error,
    items: Array.isArray(data?.items) ? data.items : [],
    merch_count: data?.merch_count,
    image_url: data?.image_url,
  };
}

/**
 * Merch mockup generation with memory + sessionStorage cache.
 * Same artwork + products reuses cached image URLs (no second generate call).
 * Apparel + stickers go in one request; `pad=true` when sticker products are included.
 */
export async function generateMerchMockups(options: {
  artwork: string | Blob | File;
  products: Product[];
  station?: string;
  /** Skip cache read/write when true. */
  forceRefresh?: boolean;
  isTransparent?: boolean;
  /** Defaults true; TEMP pet caricature test uses false. */
  alreadyTransparent?: boolean;
}): Promise<MerchGenerateResponse> {
  const station = options.station || "merch";
  const isTransparent =
    typeof options.isTransparent === "boolean"
      ? options.isTransparent
      : readFavouriteTransparencyFlag();
  const alreadyTransparent = options.alreadyTransparent !== false;
  const base = AI_SERVICES_ORIGIN.replace(/\/+$/, "");
  if (!base) {
    return {
      success: false,
      error:
        "AI services origin is not configured (REACT_APP_AI_SERVICES_ORIGIN)",
    };
  }

  const { products: productKeys, colors } = buildMerchGeneratePayload(
    options.products
  );

  if (!productKeys.length) {
    return { success: false, error: "No merch products to generate" };
  }

  const cacheKey = buildMerchGenerateCacheKey(
    options.artwork,
    options.products,
    station,
    isTransparent,
    alreadyTransparent
  );

  if (!options.forceRefresh) {
    const cached = readMerchGenerateCache(cacheKey);
    if (cached) {
      preloadMerchGenerateImages(cached.items);
      return { ...cached, message: cached.message || "Loaded from cache" };
    }

    const inFlight = merchGenerateInFlight.get(cacheKey);
    if (inFlight) return inFlight;
  }

  const run = (async (): Promise<MerchGenerateResponse> => {
    const artworkFile = await artworkToFile(options.artwork);
    const needsPad = productKeys.some(isPaddedStickerProductKey);

    const result = await requestMerchGenerate({
      base,
      artworkFile,
      station,
      productKeys,
      colors,
      isTransparent,
      alreadyTransparent,
      pad: needsPad,
    });

    if (result.success && result.items?.length) {
      writeMerchGenerateCache(cacheKey, result);
      preloadMerchGenerateImages(result.items);
    }

    return result;
  })();

  if (!options.forceRefresh) {
    merchGenerateInFlight.set(cacheKey, run);
  }

  try {
    return await run;
  } finally {
    if (merchGenerateInFlight.get(cacheKey) === run) {
      merchGenerateInFlight.delete(cacheKey);
    }
  }
}
